import * as React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import {useTranslation} from "react-i18next";
import type {ReconciliationData} from "../../../../hooks/usePayrollRun";

interface PayrollRunReconciliationMobileProps {
    reconciliation: ReconciliationData;
    employeeCount: number;
    formatEUR: (value: number | null | undefined) => string;
}

interface LineItem {
    label: string;
    value: string;
    prefix?: "+" | "-";
    bold?: boolean;
}

interface RowDef {
    icon: React.ReactElement;
    title: string;
    accentColor: string;
    headlineValue: string;
    subtitle?: string;
    lines: LineItem[];
}

// Mobile-only presentation of the same `reconciliation` data PayrollRunReconciliation renders as
// 4 side-by-side cards on desktop — collapsed into colored, expandable rows here to fit narrow
// screens (see the mobile Payroll Run reference). Reads the exact same totals; the desktop
// component/math is untouched (see docs/PAYROLL_RUN_LAYOUT.md).
export default function PayrollRunReconciliationMobile({
    reconciliation,
    employeeCount,
    formatEUR,
}: Readonly<PayrollRunReconciliationMobileProps>): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const {earnings, employeeDeductions, otherAdjustments, employerCost} = reconciliation;

    const employeeDeductionsTotal = employeeDeductions.pensionEmployee + employeeDeductions.incomeTax;
    const otherAdjustmentsTotal = Math.abs(
        otherAdjustments.familyInsurance + otherAdjustments.voluntaryFund
        - otherAdjustments.netAdditions - otherAdjustments.netDeductions,
    );

    const rows: RowDef[] = [
        {
            icon: <TrendingUpIcon fontSize="small"/>,
            title: t('payroll.run.reconciliation.title1'),
            accentColor: theme.palette.success.main,
            headlineValue: formatEUR(earnings.finalGross),
            lines: [
                {label: t('payroll.run.reconciliation.baseGross'), value: formatEUR(earnings.baseGross)},
                {label: t('payroll.run.reconciliation.grossAdditions'), prefix: "+", value: formatEUR(earnings.grossAdditions)},
                {label: t('payroll.run.reconciliation.grossDeductions'), prefix: "-", value: formatEUR(earnings.grossDeductions)},
                {label: t('payroll.run.reconciliation.netToGrossAdjustment'), prefix: "+", value: formatEUR(earnings.netToGrossAdjustment)},
                {label: t('payroll.run.table.header.finalGross'), bold: true, value: formatEUR(earnings.finalGross)},
            ],
        },
        {
            icon: <PersonIcon fontSize="small"/>,
            title: t('payroll.run.reconciliation.title2'),
            accentColor: theme.palette.error.main,
            headlineValue: formatEUR(employeeDeductionsTotal),
            subtitle: `${t('payroll.run.reconciliation.netBeforeOtherDeductions')} ${formatEUR(employeeDeductions.netBeforeOtherDeductions)}`,
            lines: [
                {label: t('payroll.run.reconciliation.employeePension'), prefix: "-", value: formatEUR(employeeDeductions.pensionEmployee)},
                {label: t('payroll.run.summary.incomeTax'), prefix: "-", value: formatEUR(employeeDeductions.incomeTax)},
                {label: t('payroll.run.reconciliation.netBeforeOtherDeductions'), bold: true, value: formatEUR(employeeDeductions.netBeforeOtherDeductions)},
            ],
        },
        {
            icon: <DescriptionOutlinedIcon fontSize="small"/>,
            title: t('payroll.run.reconciliation.title3'),
            accentColor: theme.palette.info.main,
            headlineValue: formatEUR(otherAdjustmentsTotal),
            subtitle: `${t('payroll.run.table.header.netToBank')} ${formatEUR(otherAdjustments.netToBank)}`,
            lines: [
                {label: t('payroll.run.reconciliation.netAdditions'), prefix: "+", value: formatEUR(otherAdjustments.netAdditions)},
                {label: t('payroll.run.reconciliation.netDeductions'), prefix: "-", value: formatEUR(otherAdjustments.netDeductions)},
                {label: t('payroll.run.reconciliation.familyInsurance'), prefix: "-", value: formatEUR(otherAdjustments.familyInsurance)},
                {label: t('payroll.run.reconciliation.voluntaryFund'), prefix: "-", value: formatEUR(otherAdjustments.voluntaryFund)},
                {label: t('payroll.run.table.header.netToBank'), bold: true, value: formatEUR(otherAdjustments.netToBank)},
            ],
        },
        {
            icon: <CalculateIcon fontSize="small"/>,
            title: t('payroll.run.reconciliation.title4'),
            accentColor: theme.palette.warning.main,
            headlineValue: formatEUR(employerCost.totalEmployerCost),
            lines: [
                {label: t('payroll.run.table.header.finalGross'), value: formatEUR(employerCost.finalGross)},
                {label: t('payroll.run.summary.employerPension'), prefix: "+", value: formatEUR(employerCost.employerPension)},
                {label: t('payroll.run.reconciliation.employerPaidBenefits'), prefix: "+", value: formatEUR(employerCost.employerPaidBenefits)},
                {label: t('payroll.run.summary.totalEmployerCost'), bold: true, value: formatEUR(employerCost.totalEmployerCost)},
            ],
        },
    ];

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{textAlign: "left"}}>
                {t('payroll.run.reconciliation.sectionTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{textAlign: "left", mb: 1.5}}>
                {t('payroll.run.reconciliation.subtitle', {count: employeeCount})}
            </Typography>

            <Stack spacing={1.25}>
                {rows.map((row) => {
                    const bg = alpha(row.accentColor, 0.08);
                    return (
                        <Accordion
                            key={row.title}
                            disableGutters
                            variant="outlined"
                            sx={{borderRadius: 1, backgroundColor: bg, '&:before': {display: 'none'}}}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{width: '100%', pr: 1}}>
                                    <Box sx={{color: row.accentColor, display: 'flex'}}>{row.icon}</Box>
                                    <Box sx={{flexGrow: 1, textAlign: 'left', minWidth: 0}}>
                                        <Typography variant="body2" fontWeight={700} sx={{color: row.accentColor}} noWrap>
                                            {row.title}
                                        </Typography>
                                        {row.subtitle && (
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {row.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{color: row.accentColor}} noWrap>
                                        {row.headlineValue}
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{backgroundColor: 'background.paper', pt: 1}}>
                                <Stack spacing={0.75}>
                                    {row.lines.map((line) => (
                                        <Box key={line.label} sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
                                            <Typography
                                                variant="body2"
                                                color={line.bold ? 'text.primary' : 'text.secondary'}
                                                fontWeight={line.bold ? 700 : 400}
                                                sx={{textAlign: 'left'}}
                                            >
                                                {line.prefix ? `${line.prefix} ${line.label}` : line.label}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={line.bold ? 700 : 500}>
                                                {line.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </Box>
    );
}
